import { Component, Input, ViewChild, OnInit, inject } from "@angular/core";
import { ThirdPartyModel } from "@models/thirdparty/ThirdpartyModel";
import { ThirdPartyContractsComponent } from "../thirdparty-contracts/thirdparty-contracts.component";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { FormGroup } from "@angular/forms";
import { ThirdPartyContractModel } from "@models/thirdparty/ThirdPartyContractModel";
import { ThirdPartyPermissions } from "@models/thirdparty/ThirdPartyPermissions";
import { ThirdPartyDataService } from "../services/ThirdPartyDataService";
import { IHasGuid } from "@app/shared/interfaces/IHasGuid";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { ThirdPartyContractDataService } from "../services/ThirdPartyContractDataService";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { GenericListManagerComponent } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.component";
import { injectTranslation } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/thirdparty/i18n/scope";

@Component({
    selector: "thirdparty-contracts-overview",
    templateUrl: "./thirdparty-contracts-overview.component.html",
    styleUrls: ["./thirdparty-contracts-overview.component.scss"],
    imports: [GenericListManagerComponent],
})
export class ThirdPartyContractsOverviewComponent implements OnInit {
    private _tabService = inject(TabService);
    private pages = inject(Pages);
    private _CDS = inject(ThirdPartyContractDataService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    @ViewChild("thirdpartyContractsComponent")
    ThirdPartyContractsComponent: ThirdPartyContractsComponent;

    @Input() thirdParty: ThirdPartyModel;
    @Input() permissions: ThirdPartyPermissions;
    @Input() canEdit: boolean;

    parentForm: FormGroup;

    disabled: Boolean;

    config: any;

    contractsConfig: GenericListConfig;

    async ngOnInit() {
        this.canEdit = this.permissions.canEdit;
        await this._translation.ready();
        this.initContractsConfig();
    }

    load() {
        this.config = {
            thirdPartyGuid: this.thirdParty.Guid,
        };
    }

    openTab(item: IHasGuid) {
        this._tabService.generateTab(
            this.pages.ThirdpartyContractDetail,
            item.guid,
            null,
            this.config
        );
    }

    initContractsConfig() {
        this.contractsConfig = <GenericListConfig>{
            idProp: "ID",
            uiidAdd: "thirdpartycontractoverview-addbutton",
            uiidDelete: "thirdpartycontractoverview-deletebutton",
            name: this._translation.t("contractsOverview.listName"),
            isSortable: true,
            hideRefreshButton: false,

            allowAdd: this.canEdit,
            allowDelete: this.canEdit,
            allowEdit: this.canEdit,

            fields: [
                {
                    fieldName: "ID",
                    prettyName: this._translation.t("contractsOverview.idField"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "ContractNumber",
                    prettyName: this._translation.t("contractsOverview.contractNumberField"),
                    fieldType: GenericListFieldType.Text,
                    editorWidth: "12",
                },
                {
                    fieldName: "ContractedValue",
                    prettyName: this._translation.t("contractsOverview.contractedValueField"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "AnnualValue",
                    prettyName: this._translation.t("contractsOverview.annualValueField"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "Status",
                    prettyName: this._translation.t("contractsOverview.statusField"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "Services",
                    prettyName: this._translation.t("contractsOverview.servicesField"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "ContractOwner",
                    prettyName: this._translation.t("contractsOverview.contractOwnerField"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "EndDate",
                    prettyName: this._translation.t("contractsOverview.endDateField"),
                    fieldType: GenericListFieldType.Date,
                },
            ],
            dataMethod: () => this._CDS.getContracts(this.thirdParty.Guid),

            addOverride: async () => {
                this._tabService.generateTab(
                    this.pages.ThirdpartyContractDetail,
                    null,
                    null,
                    this.config
                );
                return true;
            },

            editOverride: async (row: ThirdPartyContractModel) => {
                this._tabService.generateTab(
                    this.pages.ThirdpartyContractDetail,
                    row.guid,
                    null,
                    this.config
                );

                return true;
            },

            delete: async (row: ThirdPartyContractModel) => {
                const continueDelete = await this._promptService
                    .confirm(
                        this._translation.t("contractsOverview.deleteTitle"),
                        this._translation.t("contractsOverview.deleteMessage"),
                    )
                    .toPromise();

                if (continueDelete) {
                    this._CDS.deleteContract(row.guid).subscribe();
                    return true;
                }
                return false;
            },
        };
    }
}
